import React, { useEffect, useRef, useState } from 'react';
import { useSessionContext } from '@supabase/auth-helpers-react';
import { supabase } from '../supabaseClient';
import { useReactToPrint } from 'react-to-print';
import { handleMobileFriendlyPrint, isMobileDevice } from '../utils/printHelper';
import { exportToWord } from '../utils/wordExport';
import Footer from '../components/Footer'
import CVTemplateDefault from '../components/cv-templates/CVTemplateDefault';
import CVTemplateModern from '../components/cv-templates/CVTemplateModern';
import CVTemplateMinimalist from '../components/cv-templates/CVTemplateMinimalist';
import CVTemplateCreative from '../components/cv-templates/CVTemplateCreative';
import CVTemplateATS from '../components/cv-templates/CVTemplateATS';
import CVTemplateProfessional from '../components/cv-templates/CVTemplateProfessional';
import CVTemplateATSSimple from '../components/cv-templates/CVTemplateATSSimple';
import CVTemplateATSClassic from '../components/cv-templates/CVTemplateATSClassic';
import CVTemplateATSModern from '../components/cv-templates/CVTemplateATSModern';
import CVTemplateATSCompact from '../components/cv-templates/CVTemplateATSCompact';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  Upload,
  Globe,
  LayoutTemplate,
  ArrowLeft,
  FileText
} from 'lucide-react';

const fallbackAvatar = 'https://ui-avatars.com/api/?name=User&background=random';

export default function CVPreview() {
  const { session, isLoading: authLoading } = useSessionContext();
  const user = session?.user;
  const [userData, setUserData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState('id');
  const [template, setTemplate] = useState('default');
  const printRef = useRef();
  const navigate = useNavigate();

  const handlePrint = () => {
    if (isMobileDevice()) {
      handleMobileFriendlyPrint({
        documentTitle: `CV_${userData?.full_name || 'Profile'}`,
      });
    } else {
      handleDesktopPrint();
    }
  };

  const handleDesktopPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `CV_${userData?.full_name || 'Profile'}`,
  });

  const handleExportWord = async () => {
    if (!userData) {
      alert('Data CV belum tersedia');
      return;
    }

    try {
      const result = await exportToWord(userData, projects, certificates, language);
      if (result.success) {
        // Success - file downloaded automatically
      } else {
        alert('Gagal export ke Word: ' + result.error);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Terjadi kesalahan saat export ke Word');
    }
  };

  const handleUploadJson = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target.result);
        setUserData((prev) => ({ ...prev, ...json }));
      } catch (err) {
        alert('Gagal membaca file JSON');
      }
    };
    reader.readAsText(file);
  };

  useEffect(() => {
    if (authLoading || !user) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: users, error: userErr } = await supabase
          .from('users').select('*').eq('id', user.id).single();
        const { data: userProjects, error: projErr } = await supabase
          .from('projects').select('*').eq('user_id', user.id);
        const { data: userCertificates, error: certErr } = await supabase
          .from('certificates').select('*').eq('user_id', user.id).order('issue_date', { ascending: false });

        if (userErr || projErr) throw userErr || projErr;
        if (certErr) console.error('Error fetching certificates:', certErr);

        setUserData({
          ...users,
          photo: users.photo || fallbackAvatar,
          skills: Array.isArray(users.skills) ? users.skills : [],
          experience: Array.isArray(users.experience) ? users.experience : [],
          education: Array.isArray(users.education) ? users.education : [],
        });
        setProjects(userProjects || []);
        setCertificates(userCertificates || []);
      } catch (err) {
        console.error(err);
        setError('Gagal memuat data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authLoading, user]);

  // Template Selection Logic
  const getTemplate = () => {
    switch(template) {
      case 'modern':
        return CVTemplateModern;
      case 'minimalist':
        return CVTemplateMinimalist;
      case 'creative':
        return CVTemplateCreative;
      case 'ats':
        return CVTemplateATS;
      case 'professional':
        return CVTemplateProfessional;
      case 'ats-simple':
        return CVTemplateATSSimple;
      case 'ats-classic':
        return CVTemplateATSClassic;
      case 'ats-modern':
        return CVTemplateATSModern;
      case 'ats-compact':
        return CVTemplateATSCompact;
      default:
        return CVTemplateDefault;
    }
  };

  const Template = getTemplate();

  return (
    <div className="min-h-screen bg-white relative overflow-hidden flex flex-col">
      {(authLoading || loading) ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="relative inline-block">
              <div className="w-16 h-16 relative">
                <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-blue-500 border-r-blue-500 animate-spin"></div>
                <div className="absolute inset-2 rounded-full border-[3px] border-transparent border-b-blue-500 border-l-pink-500 animate-spin-reverse"></div>
                <div className="absolute inset-4 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 animate-pulse shadow-lg"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full shadow-md animate-ping"></div>
                </div>
              </div>
            </div>
            <p className="mt-6 text-gray-700 font-medium text-base animate-pulse">Loading CV Preview...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-red-500 text-lg mb-4">⚠️</div>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      ) : (
        <>
      {/* Floating Gradient Orbs Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none screen-visible no-print">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-gradient-to-br from-blue-500/30 via-blue-500/30 to-cyan-500/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute top-40 -right-20 w-[500px] h-[500px] bg-gradient-to-bl from-cyan-500/30 via-blue-500/30 to-blue-500/30 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-500/20 via-blue-500/20 to-cyan-500/20 rounded-full blur-3xl animate-float-slow"></div>
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(99, 102, 241, 0.02) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(99, 102, 241, 0.02) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <main className="relative z-10 p-4 sm:p-6 lg:p-8 flex-1">
      <div className="space-y-6">
        
        {/* Header Banner */}
        <div className="relative backdrop-blur-xl bg-gradient-to-r from-blue-600/90 via-blue-600/90 to-cyan-600/90 rounded-3xl shadow-xl p-6 sm:p-8 text-white border border-white/20 overflow-hidden screen-visible no-print">
          {/* Shimmer Effect */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none"></div>
          
          <div className="flex justify-between items-center flex-wrap gap-4 relative z-10">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/profile')}
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2.5 rounded-lg shadow-md transition-all group"
                title="Back to Profile"
              >
                <ArrowLeft size={20} className="group-hover:-translate-x-0.5 transition-transform" />
              </button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">CV Preview & Export</h1>
                <p className="text-blue-100 text-sm mt-1">Preview dan download CV dalam berbagai format</p>
              </div>
            </div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="relative backdrop-blur-xl bg-white/70 rounded-2xl shadow-2xl p-6 border border-white/40 overflow-hidden screen-visible no-print">
          {/* Shimmer Effect */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none"></div>
          
          <div className="flex flex-wrap justify-between items-center gap-4 relative z-10">
            
            {/* Left Controls */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Language Selector */}
              <div className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-lg border border-gray-200">
                <Globe className="w-5 h-5 text-blue-600" />
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="bg-transparent border-0 focus:outline-none focus:ring-0 text-sm font-medium text-gray-700 cursor-pointer"
                >
                  <option value="id">🇮🇩 Bahasa</option>
                  <option value="en">🇬🇧 English</option>
                </select>
              </div>

              {/* Template Selector */}
              <div className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-lg border border-gray-200">
                <LayoutTemplate className="w-5 h-5 text-blue-600" />
                <select
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                  className="bg-transparent border-0 focus:outline-none focus:ring-0 text-sm font-medium text-gray-700 cursor-pointer"
                >
                  <option value="default">📄 Default</option>
                  <option value="modern">✨ Modern</option>
                  <option value="minimalist">⚡ Minimalist</option>
                  <option value="creative">🎨 Creative</option>
                  <option value="professional">💼 Professional</option>
                  <optgroup label="🎯 ATS Friendly Templates">
                    <option value="ats">🎯 ATS (dengan foto)</option>
                    <option value="ats-simple">📋 ATS Simple (tanpa foto)</option>
                    <option value="ats-classic">📰 ATS Classic (foto atas)</option>
                    <option value="ats-modern">� ATS Modern (shaded)</option>
                    <option value="ats-compact">📑 ATS Compact (ringkas)</option>
                  </optgroup>
                </select>
              </div>

              {/* Upload JSON */}
              <label
                title="Upload Data CV dari File JSON"
                className="flex items-center gap-2 cursor-pointer px-4 py-2.5 bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 rounded-lg shadow-sm transition-all text-sm font-medium text-gray-700"
              >
                <Upload className="w-5 h-5" />
                <span className="hidden sm:inline">Upload JSON</span>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleUploadJson}
                  className="hidden"
                />
              </label>
            </div>

            {/* Right Controls */}
            <div className="flex gap-2">
              {/* Export Word Button */}
              <button
                onClick={handleExportWord}
                title="Export CV sebagai Word Document"
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-5 py-2.5 rounded-lg shadow-md transition-all text-sm font-semibold"
              >
                <FileText className="w-5 h-5" />
                <span className="hidden sm:inline">Export Word</span>
                <span className="sm:hidden">Word</span>
              </button>

              {/* Download PDF Button */}
              <button
                onClick={handlePrint}
                title="Download CV sebagai PDF"
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-5 py-2.5 rounded-lg shadow-md transition-all text-sm font-semibold"
              >
                <Download className="w-5 h-5" />
                <span className="hidden sm:inline">Download PDF</span>
                <span className="sm:hidden">PDF</span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Export Hint */}
        <div className="block sm:hidden bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 screen-visible no-print">
          <p className="text-xs text-blue-800 flex items-start gap-2">
            <span className="text-base flex-shrink-0">💡</span>
            <span>
              <strong>Tips Mobile:</strong> Gunakan tombol <strong>Export Word</strong> untuk hasil terbaik di perangkat mobile. File .docx bisa dibuka di Word/Google Docs dan di-print dari sana.
            </span>
          </p>
        </div>

        {/* Template Info Card */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-50 rounded-xl p-4 border border-blue-100 screen-visible no-print">
          <div className="flex items-start gap-3">
            <div className="text-2xl">
              {template === 'default' && '📄'}
              {template === 'modern' && '✨'}
              {template === 'minimalist' && '⚡'}
              {template === 'creative' && '🎨'}
              {template === 'ats' && '🎯'}
              {template === 'professional' && '💼'}
              {template === 'ats-simple' && '📋'}
              {template === 'ats-classic' && '📰'}
              {template === 'ats-modern' && '🔷'}
              {template === 'ats-compact' && '📑'}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">
                {template === 'default' && 'Default Template'}
                {template === 'modern' && 'Modern Template'}
                {template === 'minimalist' && 'Minimalist Template'}
                {template === 'creative' && 'Creative Template'}
                {template === 'ats' && 'ATS Friendly (with Photo)'}
                {template === 'professional' && 'Professional Template'}
                {template === 'ats-simple' && 'ATS Simple (No Photo)'}
                {template === 'ats-classic' && 'ATS Classic (Photo on Top)'}
                {template === 'ats-modern' && 'ATS Modern (Shaded Sections)'}
                {template === 'ats-compact' && 'ATS Compact (Space Efficient)'}
              </h3>
              <p className="text-sm text-gray-600">
                {template === 'default' && 'Template klasik dengan layout dua kolom yang clean dan mudah dibaca.'}
                {template === 'modern' && 'Design modern dengan accent warna dan typography yang eye-catching.'}
                {template === 'minimalist' && 'Ultra-minimalis dengan fokus pada konten dan readability.'}
                {template === 'creative' && 'Template kreatif dengan gradient colors dan visual elements yang menarik. Cocok untuk creative roles.'}
                {template === 'ats' && 'Foto di atas, format simple tanpa fancy design. Optimal untuk ATS (Applicant Tracking System).'}
                {template === 'professional' && 'Template profesional bergaya klasik dengan blue accent. Ideal untuk corporate roles.'}
                {template === 'ats-simple' && 'Format ATS paling sederhana tanpa foto, menggunakan Calibri font. Maksimal kompatibilitas dengan semua ATS. Cocok untuk perusahaan yang melarang foto atau aplikasi internasional.'}
                {template === 'ats-classic' && 'Format klasik dengan foto di atas menggunakan Times New Roman. Pendidikan diutamakan. Ideal untuk posisi tradisional dan perusahaan konservatif.'}
                {template === 'ats-modern' && 'ATS-friendly dengan sentuhan modern menggunakan shaded sections. Balance antara style dan parsability. Cocok untuk perusahaan tech-savvy.'}
                {template === 'ats-compact' && 'Format ringkas untuk memuat banyak informasi dalam 1 halaman. Font kecil tapi tetap readable. Ideal untuk professionals dengan banyak pengalaman.'}
              </p>
            </div>
          </div>
        </div>

        {/* CV Preview Container */}
        <div className="cv-preview-container relative backdrop-blur-xl bg-white/70 rounded-3xl shadow-2xl p-4 sm:p-8 border border-white/40 overflow-hidden">
          {/* Shimmer Effect */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none screen-visible no-print"></div>
          
          <div ref={printRef} className="cv-content-wrapper relative z-10">
            <Template user={userData} projects={projects} certificates={certificates} language={language} />
          </div>
        </div>

      </div>
      </main>
      <Footer />
      </>
      )}
    </div>
  );
}
